//import type IJellies from "@tmna/seal-interfaces/lib/IJellies";
//import type IJellyParams from "@tmna/seal-interfaces/lib/IJellyParams";
import type { HttpStatusCode } from "axios";
// Temporarily copying types in for SEAL
export interface IJellies {
	imageSrc: string;
	backupSrc: string;
}
export interface IJellyParams {
	model: string;
	year?: number;
	trim?: string;
	color?: string;
	width?: number;
	height?: number;
	angle?: string;
}

export enum EOSEndpointServiceIntent {
	VEHICLE_MANAGEMENT = "VEHICLE_MANAGEMENT",
	VEHICLE_HEALTH = "VEHICLE_HEALTH",
	VEHICLE_MAINTENANCE = "VEHICLE_MAINTENANCE",
	SERVICE_MANAGEMENT = "SERVICE_MANAGEMENT",
	VEHICLE_INFO = "VEHICLE_INFO",
	VEHICLE_SUBSCRIPTION = "VEHICLE_SUBSCRIPTION",
}

export type RecaptchaTokenData = {
	data?: {
		[key: string]: unknown;
	};
};

export type VehicleType = {
	vin?: string;
	vehicleId?: string;
	model: string;
	year: string;
	exteriorColorCode?: string;
};

export type ApiClientInterface = {
	oauth?: string;
	captcha?: boolean;
	headers?: {
		[key: string]: unknown;
	};
	opts?: {
		[key: string]: unknown;
	};
	params?: {
		[key: string]: unknown;
	};
};

export interface EosServerResponseInterface {
	data?: EosServerDataInterface;
	error?: EosServerErrorInterface;
}

export interface EosServerDataInterface {
	meta?: any;
	data?: any;
}

export interface EosServerErrorInterface {
	code: string;
	detail: string;
	detailedDescription: string;
}

export interface GetHeadersInterface {
	headers: object;
	privateKey?: boolean;
	mode?: string;
}

export type ServiceLineItems = {
	opCode: string;
	serviceHistoryOpcodeId: string;
	serviceCategoryDesc: string;
	serviceOther: string;
	serviceCategory: string;
	serviceOpCodeName: string;
};

export interface EOSServiceHistoryFormData {
	serviceMonth: string;
	serviceDay: string;
	serviceYear: string;
	serviceMileage: number;
	serviceProvider: string;
	repairOrderNumber: string;
	serviceHistoryDetailList: ServiceLineItems[];
}

export type memberServiceHistoryId = {
	memberServiceHistoryId: string;
};

export interface ServiceManagementProps {
	vin?: string;
	oauth?: string;
	captcha?: boolean;
	memberServiceHistoryId?: string;
}

export interface VehicleManagementProps {
	vehicleId?: string;
	vin?: string;
	model?: string;
	year?: string;
	privateKey?: boolean;
}

export interface VehicleHealthProps {
	vin: string;
}

export interface VehicleInfoProps {
	oauth: string;
}

export interface VehicleMaintenanceProps {
	oauth: string;
	captcha?: boolean;
}

type DealerAttribute = {
	name: string;
	code: string;
	participationIndicator: boolean;
};

type DealerAdditionalDetails = {
	HoursOperation: Array<{
		daysOfWeek: Array<{
			dayOfWeek: string;
			openTime: string;
			closeTime: string;
		}>;
		hoursTypeCode: string;
	}>;
	Phones: Array<{
		channelCode: { value: string };
		completeNumber: { value: string };
	}>;
	additionalServices: Array<{
		code: { value: string };
		name: { value: string };
		participationIndicator: boolean;
	}>;
	proximityMeasureGroup: Array<{
		geographicalCoordinate: {
			latitudeMeasure: {
				value: number;
				unitCode: string;
			};
			longitudeMeasure: {
				value: number;
				unitCode: string;
			};
		};
		proximityMeasureType: { value: string };
	}>;
};

export type Dealer = {
	additionalDetails: DealerAdditionalDetails;
	address: {
		addressLine1?: string;
		addressLine2?: string;
		city?: string;
		countryCode?: string;
		state?: string;
		zipCode?: string;
	};
	dealerAttributes?: Array<DealerAttribute>;
	dealerCode?: string;
	dealerName?: string;
	districtCode?: string;
	emailURL?: null | string;
	faxNumber?: null | string;
	financeURL?: null | string;
	hours?: string;
	mapURL?: null | string;
	phoneNumber: {
		areaCode?: string;
		phoneNumber?: string;
	};
	phoneNumberFinance: null | string | Record<never, never>;
	phoneNumberParts: null | string | Record<never, never>;
	phoneNumberService: null | string | Record<never, never>;
	phoneNumberTollFree: null | string | Record<never, never>;
	regionCode?: string;
	relationshipTypeCode?: string;
	serviceURL?: string;
	spanishSpeaking?: boolean;
	specialsURL?: null | string;
	uri?: null | string;
	worldLatitude?: null | string;
};

export type VehicleWithVin = {
	brand: "L" | "T";
	createdDate: null | number;
	customerProductRole: "OWNER" | string;
	description: string;
	driveType: string;
	enformDiagnosticsFull: boolean;
	enformDiagnosticsLight: boolean;
	enformDiagnosticsLightPlus: boolean;
	entuneEnformInd: boolean;
	exteriorColorCode: string;
	grade: string;
	headUnitType: boolean;
	homeDealerCode: string;
	installationSource: Array<string>;
	installationSourceType: Array<string>;
	jellyImageUrl: null | IJellies;
	keyOffCapableIndicator: boolean;
	make: "LEXUS" | "TOYOTA";
	model: string;
	modelCode: string;
	nickName: null | string | "";
	preferredDealer: null | Dealer;
	preferredDealerCode: string;
	preferredVehicle: "Y" | "N";
	safetyConnectInd: boolean;
	serviceConnectEnabled: boolean;
	transmission: string;
	vdsection: string;
	vehicleId: string;
	vin: string;
	wmidentifier: string;
	year: string;
	yearMakeModel: string;
};

export type Vehicle =
	| (Pick<
			VehicleWithVin,
			| "brand"
			| "createdDate"
			| "make"
			| "model"
			| "preferredVehicle"
			| "vehicleId"
			| "year"
			| "yearMakeModel"
	  > & { nickName?: string | "" })
	| VehicleWithVin;

export type GetVehicleDealerImperativeResponse = {
	sellingDealer: Dealer | null;
	preferredDealer: Dealer | null;
	homeDealer: Dealer | null;
};

export interface UserVehicleData {
	status: string;
	vehicles: VehicleWithVin[] | [];
	isInitialLoadingUserVehicles: boolean;
}

export type VehicleSpecDetailResponse = {
	//could be this one (double check)
	availableService: boolean;
	vin: string;
	make: string;
	modelMake: "LEXUS" | "TOYOTA";
	model: string;
	year: string;
	modelCode: string;
	vehicleExteriorColorDescription: string;
	vehicleInteriorColorDescription: string;
	exteriorColorCode: string;
	interiorColorCode: string;
	vehicleGrade: string;
	vehicleTransmission: string;
	vehicleBodyType: string;
	vehicleDriveType: string;
	vehicleEngine: string;
	vehicleEngineSource: string;
	modelEdition: string;
	modelDescription: string;
	vehicleSellDlrCode: string;
	vehicleTransmissionDesc: string;
	vehicleDateOfFirstUse: string;
	vehicleTotalMsrp: string;
	vehicleMsrp: string;
	trimCode: string;
	vehicleEngineAbbr: string;
	vehicleOrderDate: string;
	vehicleManufacturePlantName: string;
	vehicleManufacturePlantSource: string;
	vehiclePortDamage: string;
	vehicleTireBrand: string;
	vehicletireSpareBrand: string;
	installationSource: Array<string>;
	installationSourceType: Array<string>;
	descriptionTitleDesc: Array<string>;
	descriptionTitle: Array<string>;
	isVehicleAssociated: "true" | "false";
	isVehicleSpecDataAvl: "true" | "false";
	isVehicleDelaersAvl: "true" | "false";
	isVehicledataAvl: "true" | "false";
	hotSpotWifiCapability: "Y" | "N";
	vehicleHealthInformation: "Y" | "N";
	keyOffCapableIndicator: boolean;
	entuneEnformInd: boolean;
	safetyConnectInd: boolean;
	remoteServicesIndicator: boolean;
	enformDiagnosticsFull: boolean;
	enformDiagnosticsLight: boolean;
	enformDiagnosticsLightPlus: boolean;
	safetyConnect: boolean;
	satRadioServices: [
		{
			autoPayIndicator: "Y" | "N";
			description: string;
			effectiveDate: string;
			expirationDate: string;
			programCode: string;
			status: string;
			subscriptionDate: string;
		},
	];
	entune: boolean;
	foreignSoldInd: "true" | "false";
	bodystyleDescription: string;
	sellAreaDescription: string;
	curDlrCode: string;
};

export type GetVehicleSpecificationsResponse = {
	data: {
		VehicleSpecDetailResponse: VehicleSpecDetailResponse;
	};
	meta: {
		region: string;
		requestId: string;
	};
};

export type UpdateUserVehicleNameResponse = {
	body: {
		data: {
			customerId: string;
			make: "L" | "T";
			model: string;
			nickName: string;
			preferredVehicle: "Y" | "N";
			vehicleId: string;
			vin: string;
		};
	};
	meta: {
		region: string;
		requestId: string;
	};
	status: HttpStatusCode;
};

// VHR Interfaces
interface MaintenanceInformation {
	alertIcon: string;
	currentMileage: number;
	currentMileageUnit: string;
	currentServiceMileageUnit: string;
	mileageUnit: string;
	mntServiceVerbiage: string;
	nextServiceDueFlag: boolean;
	popupDescription: string;
	popupTitle: string;
	sectionTitle: string;
	serviceDueDetails: {
		mileageMilestone: {
			mileageUnit: string;
			showInterval: boolean;
		};
	};
}

interface SafetyRecalls {
	popupDescription: string;
	sectionTitle: string;
	safetyRecallList: {
		NHTSARecallDate: string;
		RemedyAvailable: string;
		DealerReferenceID: string;
		Description: string;
		FAQUrl?: string;
		Title: string;
		NHSTARecallNumber: string;
		Remedy: string;
	}[];
	popupTitle: string;
	alertIcon: string;
}

interface ServiceCampaigns {
	popupDescription: string;
	sectionTitle: string;
	alertIconURL: string;
	serviceCampaignsList: any[];
	popupTitle: string;
}

interface VehicleStatus {
	popupDescription: string;
	sectionTitle: string;
	engineOilLevelDescription?: string;
	fuelIconTitle: string;
	fuelIconDescription: string;
	milesToEmptyTitle: string;
	oilDegradationDescription?: string;
	odometerReading: number;
	fuelIconURL: string;
	hoverDateDescription: string;
	smartKeyBatteryDescription?: string;
	engineOilImageURL?: string;
	oilDegradationImageURL?: string | null;
	oilDegradationStatus?: any;
	hoverPopupDescription: string;
	engineOilLevelTitle?: string;
	qtyEngineOilLastUpdateTime?: any;
	smartKeyImageURL?: string;
	milesToEmptyDescription: string;
	milesToEmptyDateTime: string;
	smartKeyBatteryTitle?: string;
	hoverPopupTitle: string;
	oilDegradationTitle?: string;
	hoverDateTitle: string;
	engineOilLevelStatus?: string;
	milesToEmptyImageURL: string;
	odometerIcon: number;
	popupTitle: string;
	alertIcon: string;
	hoverIcon: string;
	smartKeyBatteryDegradationStatus?: string;
	imgUrlEngOilLevelStatus?: string;
	imgUrlSmartKeyBatteryStatus?: string;
	imgUrlDegStatus?: string;
}

interface VehicleAlert {
	WNGDesc: string;
	WNGIcon: string;
	WNGType: string;
	WNGName: string;
	WNGDate: string;
}

interface VehicleAlert {
	popupDescription: string;
	sectionTitle: string;
	vehicleAlertList: {
		WNGDesc: string;
		WNGIcon: string;
		WNGType: string;
		WNGName: string;
		WNGDate: string;
	}[];
	popupTitle: string;
	alertIcon: string;
}

export interface VehicleHealthReportData {
	maintenanceInformation: MaintenanceInformation;
	safetyRecalls: SafetyRecalls;
	serviceCampaigns: ServiceCampaigns;
	vehicleStatus: VehicleStatus;
	vehicleAlerts: VehicleAlert;
}
export interface VehicleHealthReport {
	data: VehicleHealthReportData;
	isLoading: boolean;
	isError?: any;
}

export type getJellyT = (
	params: IJellyParams & { vehicleId: string },
) => Promise<Omit<IJellies, "backupSrc">>;

declare global {
	interface Window {
		grecaptcha: {
			// @ts-ignore: import comes from global tsconfig
			enterprise: ReCaptchaV2.ReCaptcha;
		};
	}
}
